/**
 * Skill type definitions for Idle Depths.
 *
 * Skills are the core progression system. Each skill has:
 * - A definition (from content packs) describing the skill
 * - Progress data (per character) tracking XP, level, and affinity
 * - Actions that can be performed to gain XP
 *
 * @see docs/design/skills/system-overview.md
 */

// ============================================================================
// Skill Categories
// ============================================================================

/**
 * Skill category determines UI grouping and some mechanics.
 */
export type SkillCategory = 'gathering' | 'artisan' | 'combat' | 'support';

/**
 * All skill categories with display info.
 */
export const SKILL_CATEGORIES: Record<SkillCategory, { name: string; description: string }> = {
  gathering: {
    name: 'Gathering',
    description: 'Skills for collecting raw materials',
  },
  artisan: {
    name: 'Artisan',
    description: 'Skills for crafting and processing materials',
  },
  combat: {
    name: 'Combat',
    description: 'Skills for fighting monsters and bosses',
  },
  support: {
    name: 'Support',
    description: 'Skills that enhance other activities',
  },
};

// ============================================================================
// Skill Definition (from content packs)
// ============================================================================

/**
 * Skill definition from content packs.
 *
 * Defines what a skill is and how it behaves.
 * Instances are registered by content packs.
 */
export interface SkillDefinition {
  /** Unique skill identifier (e.g., 'mining') */
  id: string;

  /** Display name (e.g., 'Mining') */
  name: string;

  /** Description of what this skill does */
  description: string;

  /** Skill category for grouping */
  category: SkillCategory;

  /** Icon identifier for UI */
  icon: string;

  /** Whether this skill starts unlocked (false for artisan skills) */
  unlockedByDefault: boolean;
}

// ============================================================================
// Action Definition (from content packs)
// ============================================================================

/**
 * Requirement types for performing actions.
 */
export type ActionRequirementType = 'level' | 'location' | 'item' | 'quest';

/**
 * Requirement to perform an action.
 */
export interface ActionRequirement {
  /** Type of requirement */
  type: ActionRequirementType;

  /** Level required (for 'level' type) */
  level?: number;

  /** Location ID required (for 'location' type) */
  locationId?: string;

  /** Item ID required (for 'item' type) */
  itemId?: string;

  /** Item quantity required (for 'item' type) */
  itemQuantity?: number;

  /** Whether item is consumed (for 'item' type) */
  consumeItem?: boolean;

  /** Quest ID required (for 'quest' type) */
  questId?: string;
}

/**
 * Output produced by an action.
 *
 * Note: Outputs are deferred to the loot system.
 * For now, this interface is defined but not processed.
 */
export interface ActionOutput {
  /** Item ID to produce */
  itemId: string;

  /** Minimum quantity */
  minQuantity: number;

  /** Maximum quantity */
  maxQuantity: number;

  /** Chance to produce (0-1) */
  chance: number;
}

/**
 * Action definition from content packs.
 *
 * Defines an action that can be performed for a skill.
 */
export interface ActionDefinition {
  /** Unique action identifier (e.g., 'mine_copper') */
  id: string;

  /** Skill this action belongs to */
  skillId: string;

  /** Display name (e.g., 'Mine Copper Ore') */
  name: string;

  /** Description of what this action does */
  description: string;

  /** Level required to perform this action */
  levelRequired: number;

  /** Base time to complete in milliseconds */
  baseTimeMs: number;

  /** Base XP awarded on completion */
  baseXP: number;

  /** Additional requirements beyond level */
  requirements: ActionRequirement[];

  /** Items produced on completion (deferred to loot system) */
  outputs: ActionOutput[];
}

// ============================================================================
// Skill Progress (per character)
// ============================================================================

/**
 * Action affinity tracking.
 *
 * Tracks completions toward mastery (1000 completions = 100% affinity).
 */
export interface ActionAffinity {
  /** Action identifier */
  actionId: string;

  /** Number of times this action was completed */
  completions: number;

  /** Affinity percentage (0-100, computed from completions) */
  affinityPercent: number;
}

/**
 * Base skill progress tracked per character.
 *
 * Extended by skill-specific progress types for additional data.
 */
export interface SkillProgress {
  /** Skill identifier (discriminator for type narrowing) */
  skillId: string;

  /** Total XP earned in this skill */
  totalXP: number;

  /** Current level (cached from totalXP for performance) */
  level: number;

  /** Whether this skill is unlocked */
  unlocked: boolean;

  /** When this skill was unlocked (timestamp, null if not unlocked yet) */
  unlockedAt: number | null;

  /** Per-action affinity tracking */
  actionAffinity: Record<string, ActionAffinity>;
}

// ============================================================================
// Skill-Specific Progress Types
// ============================================================================

/**
 * Mining-specific skill progress.
 *
 * Currently no additional data, but structured for future extension
 * (e.g., discovered special nodes, mined ore counts).
 */
export interface MiningSkillProgress extends SkillProgress {
  /** Discriminator for type narrowing */
  skillId: 'mining';
}

/**
 * Woodcutting-specific skill progress.
 */
export interface WoodcuttingSkillProgress extends SkillProgress {
  skillId: 'woodcutting';
}

/**
 * Fishing-specific skill progress.
 */
export interface FishingSkillProgress extends SkillProgress {
  skillId: 'fishing';
}

/**
 * Smithing-specific skill progress.
 *
 * Artisan skills track discovered recipes.
 */
export interface SmithingSkillProgress extends SkillProgress {
  skillId: 'smithing';
  /** Recipes discovered through experimentation */
  discoveredRecipes: string[];
}

/**
 * Alchemy-specific skill progress.
 */
export interface AlchemySkillProgress extends SkillProgress {
  skillId: 'alchemy';
  /** Recipes discovered through experimentation */
  discoveredRecipes: string[];
}

// ============================================================================
// Skilling Activity Data
// ============================================================================

/**
 * Data for a skilling activity.
 *
 * Stored in CharacterData.currentActivity when skilling.
 * Extends Record<string, unknown> for serialization compatibility.
 */
export interface SkillingActivityData extends Record<string, unknown> {
  /** Skill being trained */
  skillId: string;

  /** Action being performed */
  actionId: string;

  /** When the action started (timestamp) */
  startedAt: number;

  /** When the action will complete (timestamp) */
  completesAt: number;

  /** Base time for recalculation if bonuses change */
  baseTimeMs: number;
}

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validate that an object is a valid SkillDefinition.
 *
 * @param data - Unknown data to validate
 * @returns True if data is a valid SkillDefinition
 */
export function isSkillDefinition(data: unknown): data is SkillDefinition {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  // First check all properties are strings/booleans
  if (
    typeof obj.id !== 'string' ||
    typeof obj.name !== 'string' ||
    typeof obj.description !== 'string' ||
    typeof obj.category !== 'string' ||
    typeof obj.icon !== 'string' ||
    typeof obj.unlockedByDefault !== 'boolean'
  ) {
    return false;
  }

  // Then check category is valid
  const validCategories = ['gathering', 'artisan', 'combat', 'support'];
  return validCategories.includes(obj.category);
}

/**
 * Validate that an object is a valid ActionDefinition.
 *
 * @param data - Unknown data to validate
 * @returns True if data is a valid ActionDefinition
 */
export function isActionDefinition(data: unknown): data is ActionDefinition {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  return (
    typeof obj.id === 'string' &&
    typeof obj.skillId === 'string' &&
    typeof obj.name === 'string' &&
    typeof obj.description === 'string' &&
    typeof obj.levelRequired === 'number' &&
    obj.levelRequired >= 1 &&
    typeof obj.baseTimeMs === 'number' &&
    obj.baseTimeMs > 0 &&
    typeof obj.baseXP === 'number' &&
    obj.baseXP >= 0 &&
    Array.isArray(obj.requirements) &&
    Array.isArray(obj.outputs)
  );
}

/**
 * Validate that an object is a valid SkillProgress.
 *
 * @param data - Unknown data to validate
 * @returns True if data is a valid SkillProgress
 */
export function isSkillProgress(data: unknown): data is SkillProgress {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  return (
    typeof obj.skillId === 'string' &&
    typeof obj.totalXP === 'number' &&
    obj.totalXP >= 0 &&
    typeof obj.level === 'number' &&
    obj.level >= 1 &&
    typeof obj.unlocked === 'boolean' &&
    (obj.unlockedAt === null || typeof obj.unlockedAt === 'number') &&
    typeof obj.actionAffinity === 'object' &&
    obj.actionAffinity !== null
  );
}

/**
 * Validate that an object is a valid ActionAffinity.
 *
 * @param data - Unknown data to validate
 * @returns True if data is a valid ActionAffinity
 */
export function isActionAffinity(data: unknown): data is ActionAffinity {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  return (
    typeof obj.actionId === 'string' &&
    typeof obj.completions === 'number' &&
    obj.completions >= 0 &&
    typeof obj.affinityPercent === 'number' &&
    obj.affinityPercent >= 0 &&
    obj.affinityPercent <= 100
  );
}

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Create default skill progress for a skill.
 *
 * @param skillId - The skill ID
 * @param unlocked - Whether the skill starts unlocked
 * @returns New SkillProgress object
 */
export function createSkillProgress(skillId: string, unlocked: boolean): SkillProgress {
  return {
    skillId,
    totalXP: 0,
    level: 1,
    unlocked,
    unlockedAt: unlocked ? Date.now() : null,
    actionAffinity: {},
  };
}

/**
 * Create default action affinity for an action.
 *
 * @param actionId - The action ID
 * @returns New ActionAffinity object
 */
export function createActionAffinity(actionId: string): ActionAffinity {
  return {
    actionId,
    completions: 0,
    affinityPercent: 0,
  };
}

/**
 * Create skilling activity data.
 *
 * @param skillId - The skill being trained
 * @param actionId - The action being performed
 * @param baseTimeMs - Base time for the action in milliseconds
 * @returns New SkillingActivityData object
 */
export function createSkillingActivityData(
  skillId: string,
  actionId: string,
  baseTimeMs: number
): SkillingActivityData {
  const now = Date.now();
  return {
    skillId,
    actionId,
    startedAt: now,
    completesAt: now + baseTimeMs,
    baseTimeMs,
  };
}
